/**
 * Helpers for embedding the app's scheme/package identifier into OAuth and
 * wallet-payment return URLs (static/oauth.html, static/cnp-wallet.html).
 *
 * oauth.html is a standalone static page, opened in its own popup/webview/
 * browser context with no access to the running app's live Redux state.
 * interfaceConfig can be branding-overwritten at runtime (mid-conference),
 * so a fresh fetch of interface_config.js from that separate context can
 * disagree with what the app is actually using -- only a value placed on
 * the return URL itself travels reliably through the OAuth/gateway
 * provider's redirect. These helpers let each call site (which knows its
 * own live interfaceConfig and platform for certain) resolve that value
 * once, at the moment a service is invoked, and pass it along explicitly.
 */

const DEFAULT_APP_PACKAGE = 'go.meethour.io';

/**
 * Resolves the app scheme/package identifier for the given platform from a
 * live interfaceConfig object, falling back to the default when unset.
 *
 * @param {?Object} interfaceConfig - The live interfaceConfig object.
 * @param {string} platform - One of 'ios', 'android', 'desktop'.
 * @returns {string} The resolved scheme/package identifier (no "://").
 */
export function resolveAppPackage(interfaceConfig, platform) {
    const cfg = interfaceConfig || {};

    switch (platform) {
    case 'ios':
        return cfg.APP_SCHEME || DEFAULT_APP_PACKAGE;
    case 'android':
        return cfg.ANDROID_APP_PACKAGE || DEFAULT_APP_PACKAGE;
    case 'desktop':
        return cfg.DESKTOP_APP_SCHEME || DEFAULT_APP_PACKAGE;
    default:
        return DEFAULT_APP_PACKAGE;
    }
}

/**
 * Appends app_package/app_platform query params to a URL so a returning
 * OAuth/wallet-payment flow (oauth.html) can read the resolved scheme
 * directly, instead of independently re-deriving it.
 *
 * @param {string} url - The base URL.
 * @param {string} appPackage - The resolved scheme/package identifier (as returned by {@link resolveAppPackage}).
 * @param {string} platform - One of 'ios', 'android', 'desktop'.
 * @returns {string} The URL with app_package/app_platform appended.
 */
export function appendAppPackageParams(url, appPackage, platform) {
    const separator = url.includes('?') ? '&' : '?';

    return `${url}${separator}app_package=${encodeURIComponent(appPackage)}&app_platform=${encodeURIComponent(platform)}`;
}
